import React, { Component } from 'react';
import dateFormat from 'dateformat';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import SalonView from '../components/SalonView';
import { navigateTo } from '../utils/navigation';
import { validateSession } from '../utils/functions';
import {
  getAgenda, listReservations, getReservationDetail,
  patchReservationStatus, releaseTable, addGuests, removeGuest,
  checkAvailability, createWalkIn, getDemoGames, getClientByCedula,
  addReservationGame, removeReservationGame, getTableAccount, settleTableAccount, businessNow,
  includeOrder, getCafeteriaMenu,
  getSchedule, getBlocks, addBlock, updateBlock, removeBlock, scheduleForDate, addReservationSeats,
  removeReservationSeats, checkInReservation, moveReservation, rescheduleReservation, canReschedule,
  RESERVATION_STATUS, PAYMENT_METHOD_LABEL, RESERVATION_ORIGIN_LABEL, managedBy, paymentState,
  SETTLE_PAYMENTS, PAYMENT_METHOD_TO_TYPE, canChargeSeats, getPosDevices, pickDefaultPosDevice,
} from '../utils/reservationsApi';
import { signupPaymentChip, signupStatusChip } from '../utils/openTablesApi';
import { membershipStatus } from '../utils/membershipsApi';
import { startActivity, textoDeAviso } from '../utils/activitiesApi';

// Panel de reservas de mesa (ENI-39 follow-up). Tablero de ocupación por día,
// listado con acciones y detalle con invitados. La venta en el acto (walk-in) y la
// configuración de horario/bloqueos llegan en fases siguientes.


const pad = (n) => String(n).padStart(2, '0');
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (date, n) => { const x = new Date(date); x.setDate(x.getDate() + n); return x; };
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
// Formatea un DATEONLY ('YYYY-MM-DD') sin pasar por Date (evita el off-by-one por TZ).
const fmtYMD = (ymd) => {
  if (!ymd) return '—';
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const STATUS_FILTERS = [
  { key: '', label: 'Todas' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'active', label: 'Activas' },
  { key: 'completed', label: 'Completadas' },
  { key: 'cancelled', label: 'Canceladas' },
];

// El mismo orden que el form de sentar del salón (SalonView), que es el otro punto donde el
// mostrador elige medio de pago. POS faltaba acá aunque el backend y la caja ya lo soportan.
const PAYMENT_METHODS = ['efectivo', 'pos', 'transferencia', 'mercadopago'];
// POS es el medio más usado en el mostrador; ya era el default al sentar en el salón, en el
// POS de cafetería y al cobrar la cuenta. Este form era el único que arrancaba en efectivo.
const DEFAULT_PAYMENT_METHOD = 'pos';

const emptyWalkinForm = (date, startHour) => ({
  date,
  startHour,
  playerCount: 2,
  paymentMethod: DEFAULT_PAYMENT_METHOD,
  // La reserva del mostrador nace IMPAGA: se reserva ahora y se cobra cuando la persona
  // llega (por la cuenta de la mesa). Cobrar al crearla emitía ticket PAGADO + movimiento de
  // caja por plata que todavía no entró — el mismo criterio que ENI-222 fijó para el
  // storefront. Si el cliente está pagando en el mostrador, se elige "Cobrar ahora".
  deferCover: true,
  // ENI-268: mesa gratis — nadie de esta reserva paga entrada (tampoco quien se sume después).
  coverFree: false,
  tableId: '',           // '' = automática
  gameCode: '',
  customer: { name: '', cedula: '', accountId: null },
  guests: [],
  // Socios con mesa sin cargo para la fecha de la reserva (backend docs/spec-membresia-mesas.md):
  // cada uno suma una persona gratis; `playerCount` pasa a ser los que pagan.
  members: [],          // [{ cedula, plan }]
  memberCedula: '',
  memberError: null,
});

const emptyDetailReschedule = () => ({
  open: false, date: '', startHour: 0, busy: false, error: null, note: null,
});

const emptyDetailPayment = () => ({
  open: false, account: null, loading: false, busy: false, error: null, payType: 2,
  // El saldo a favor del titular es un medio de pago más (igual que en el salón y en el POS de
  // cafetería): se ofrece sólo si tiene saldo, y si no alcanza el resto va con `payType`.
  payWithCredit: false,
  // Lectores de tarjeta (F1 del arqueo): sólo importan si se cobra con POS, pero se piden al
  // abrir el panel porque elegirlos DESPUÉS de cobrar no existe — el ticket ya se emitió.
  posDevices: [], posDeviceId: '',
});

// ENI-206: la columna "Pago" mostraba el MÉTODO, no si la reserva está paga. El dato ya
// llegaba (paid_seats), pero el Listado nunca lo usó — sólo el panel del salón.
function PaymentBadge({ reservation }) {
  const st = paymentState(reservation);
  return (
    <span>
      <span className={`badge ${st.badge}`}>{st.label}</span>
      {st.hint && <small className="text-muted d-block" style={{ fontSize: 11 }}>{st.hint}</small>}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = RESERVATION_STATUS[status] || { label: status, badge: 'badge-light' };
  return <span className={`badge ${s.badge}`}>{s.label}</span>;
}

// FR-075 — la mesa que tomó una jornada del club. Con el adjunto `activity` dice de cuál; sin
// él cae a la etiqueta genérica, porque la query que lo arma es best-effort en el backend y su
// ausencia no cambia lo que la fila ES: una mesa del club, no una reserva a nombre de nadie.
function ActivityBadge({ reservation }) {
  const act = reservation.activity;
  return (
    <span className="badge badge-success">
      {act ? `Actividad · ${act.name}` : RESERVATION_ORIGIN_LABEL.actividad}
    </span>
  );
}

class ReservationsPage extends Component {
  constructor() {
    super();
    const today = toYMD(new Date());
    this.state = {
      checked: !!localStorage.getItem('token'),
      role: Number(localStorage.getItem('role') || 0),
      tab: 'salon',
      // salón / agenda del día (hoy)
      agenda: { tables: [], reservations: [], blocks: [] },
      agendaLoaded: false,
      selectedDay: today,
      // listado — sin fecha por default: el backend devuelve de la jornada de hoy en adelante.
      // La fecha es un filtro opcional para ir a un día puntual.
      listDate: '',
      listStatus: '',
      list: [],
      listLoaded: false,
      // detalle
      detail: null,
      detailLoaded: false,
      newGuest: { name: '', cedula: '' },
      // ENI-264: cambiar la mesa desde el detalle (el salón tiene su propio bloque en SalonView).
      // count: 0 = toda la mesa; >0 = mover sólo esas personas (ENI-269).
      detailMove: { open: false, tableId: '', count: 0, busy: false, error: null, note: null },
      // Cambiar fecha/hora de una reserva futura ("llegamos más tarde", "pasala al sábado").
      detailReschedule: emptyDetailReschedule(),
      // Cobro de las entradas desde el detalle (reserva pagada por adelantado, típicamente por
      // transferencia). `account` lo trae el backend: el importe exacto no se calcula acá.
      detailPayment: emptyDetailPayment(),
      message: null,
      // walk-in (F3)
      walkin: null,            // null = cerrado; objeto = form abierto
      walkinAvailability: null,
      walkinResult: null,
      walkinBusy: false,
      demoGames: [],
      // configuración (F4)
      scheduleForm: null,
      blocks: [],
      blocksLoaded: false,
      blockForm: { date: today, fullDay: true, startHour: 14, endHour: 22, tableId: '', description: '' },
      blockBusy: false,
      editingBlockId: null, // ENI-221: id del bloqueo que se está editando (null = alta)
      // guards de doble-submit / respuestas obsoletas
      statusBusy: false,
      guestBusy: false,
    };
    // contadores para descartar respuestas async obsoletas (last-wins)
    this._listReqId = 0;
    this._detailReqId = 0;
    this._agendaReqId = 0;
  }

  async componentDidMount() {
    const role = await validateSession(true);
    this.setState({ checked: true, role });
    // El horario define la jornada (puede cruzar medianoche), así que se carga ANTES de pedir
    // la agenda: si no, después de las 00 pediríamos el día natural y el salón saldría vacío.
    await this.loadSalonMeta();
    this.loadAgenda();
    // Auto-refresh del salón (refleja cambios de otras cajas) mientras la vista está activa.
    this._poll = setInterval(() => {
      if (this.state.tab === 'salon' && !document.hidden) this.loadAgenda();
    }, 25000);
  }

  componentWillUnmount() {
    if (this._poll) clearInterval(this._poll);
  }

  // Config de horario/precio + juegos demo para el form de "sentar" del salón.
  loadSalonMeta = async () => {
    try {
      const data = await getSchedule();
      if (data && data.schedule) this.setState({ scheduleForm: data.schedule });
    } catch (e) { /* fallback a defaults en SalonView */ }
    try {
      const data = await getDemoGames();
      this.setState({ demoGames: (data && data.games) || [] });
    } catch (e) { /* opcional */ }
  };

  // ------------------------------------------------ Hoy / Tablero (día elegido)
  loadAgenda = async () => {
    const reqId = ++this._agendaReqId;
    this.setState({ agendaLoaded: false });
    // Jornada del local: si cierra pasada la medianoche, a las 00:30 el salón sigue siendo
    // el de "ayer" (si no, las mesas sentadas desaparecerían al cambiar el día natural).
    const day = businessNow(this.state.scheduleForm).date;
    try {
      const agenda = await getAgenda(day, day); // un solo día (vista operativa)
      if (reqId !== this._agendaReqId) return;
      this.setState({ agenda: agenda || { tables: [], reservations: [], blocks: [] }, agendaLoaded: true });
    } catch (e) {
      if (reqId !== this._agendaReqId) return;
      this.setState({ agendaLoaded: true, message: { type: 'danger', text: e.message } });
    }
  };

  // Check-in: el cliente llegó → la reserva pasa a "En mesa" (seated) con la gente que vino DE
  // VERDAD. `playerCount` corrige el headcount antes de que haya plata de por medio (la entrada
  // se cobra al llegar, ENI-222); sin él es el check-in de siempre.
  // A diferencia del resto de los handlers de esta página, RE-LANZA: el form de llegada vive en
  // el panel del salón y necesita el motivo al lado del número, no como alert global.
  checkIn = async (id, playerCount = null) => {
    if (this.state.statusBusy) return;
    this.setState({ statusBusy: true });
    try {
      const result = await checkInReservation(id, playerCount);
      const seated = (result && result.reservation && result.reservation.player_count) || playerCount;
      this.setState({ statusBusy: false, message: { type: 'success', text: seated ? `En mesa: ${seated} persona(s).` : 'Mesa activada (en mesa).' } });
      this.refreshAll();
      return result;
    } catch (e) {
      this.setState({ statusBusy: false });
      throw e;
    }
  };

  /**
   * FR-068/FR-074 — "Sentar la jornada" desde el panel de una hija.
   *
   * Sienta la actividad ENTERA (cascada a las hermanas) y pone el head count en la mesa desde
   * la que se apretó. Mismo contrato que `checkIn`: RE-LANZA, porque el form vive en el panel
   * del salón y el motivo (7310 "Las mesas se sientan el dd/mm/yyyy") tiene que leerse al lado
   * del botón, no como alert global.
   *
   * Los `warnings` que devuelve el backend son las mesas que NO pudo sentar (ocupadas por un
   * walk-in, FR-060). Van al mensaje de la página: la mesa quedó sin sentar y alguien tiene que
   * hacer algo con ella.
   */
  onStartActivity = async (activityId, { tableId, headcount, everyone } = {}) => {
    const result = await startActivity(activityId, { tableId, headcount, everyone });
    const data = result || {};
    const avisos = (Array.isArray(data.warnings) ? data.warnings : []).map(textoDeAviso);
    this.setState({
      message: avisos.length
        ? { type: 'warning', text: `Jornada sentada, con avisos — ${avisos.join(' · ')}` }
        : { type: 'success', text: `Jornada sentada: ${Number(data.seated) || 0} mesa(s).` },
    });
    this.refreshAll();
    return result;
  };

  // ---------------------------------------------------------------- Salón (plano vivo)
  // Sentar gente desde el plano (walk-in "para ahora"). Lanza si el backend rechaza.
  onSeat = async (payload) => {
    const result = await createWalkIn(payload);
    const seated = result && result.reservation && result.reservation.status === 'seated';
    const text = seated
      ? `Mesa ocupada${result.table ? ': ' + result.table.label : ''}.`
      : 'Reserva creada — pendiente de pago (MercadoPago).';
    this.setState({ message: { type: 'success', text } });
    this.refreshAll();
    return result;
  };

  // Lookup de cliente por cédula para el form de sentar; devuelve {name, accountId} o null.
  lookupClient = async (cedula) => {
    try {
      const data = await getClientByCedula(cedula);
      const p = (data && data.profile) || {};
      const acc = (data && data.account) || {};
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      return { name, accountId: acc.id || null };
    } catch (e) {
      return null; // 4000 = no existe → cliente nuevo
    }
  };

  // Juegos de la sesión de mesa (F2): se van sumando mientras la mesa juega.
  onAddGame = async (reservationId, payload) => {
    const result = await addReservationGame(reservationId, payload);
    this.loadAgenda();
    return result;
  };

  onRemoveGame = async (reservationId, gameId) => {
    const result = await removeReservationGame(reservationId, gameId);
    this.loadAgenda();
    return result;
  };

  // Cuenta de la mesa (F3): estado y cobros parciales.
  onLoadAccount = async (reservationId) => {
    const data = await getTableAccount(reservationId);
    return (data && data.account) || null;
  };

  // ENI-196: sumar una persona a la mesa. El importe sale del backend (precio vigente).
  onAddSeats = async (reservationId, payload) => {
    const result = await addReservationSeats(reservationId, payload);
    const n = (result && result.added) || 0;
    this.setState({ message: { type: 'success', text: payload && payload.chargeNow
      ? `${n} entrada(s) agregada(s) y cobrada(s).`
      : `${n} entrada(s) agregada(s) a la cuenta.` } });
    this.refreshAll();
    return result;
  };

  // Espejo de onAddSeats. Re-lanza para que el motivo del rechazo —"ya se cobraron las N
  // entradas"— se vea en el propio form y no como alert global.
  onRemoveSeats = async (reservationId, count) => {
    const result = await removeReservationSeats(reservationId, count);
    const n = (result && result.removed) || 0;
    const amount = (result && result.removedAmount) || 0;
    this.setState({ message: { type: 'success', text: `${n} persona(s) menos · $${amount} menos a cobrar.` } });
    this.refreshAll();
    return result;
  };

  // Cambiar de mesa: la cuenta viaja con la reserva, así que no hay nada que reconciliar acá.
  // ENI-269: con `count` se mueven sólo esas personas (split a mesa libre o merge en una
  // sentada); los lugares viajan con su parte de la cuenta.
  onMove = async (reservationId, tableId, count) => {
    const result = await moveReservation(reservationId, tableId, count);
    const to = (result && result.to && result.to.label) || 'la mesa nueva';
    const from = result && result.from && result.from.label;
    const warning = result && result.warning;
    const moved = result && result.moved;
    const base = moved
      ? `${moved} persona${moved > 1 ? 's' : ''} ${from ? `de ${from} ` : ''}a ${to}${result.merged ? ' (se sumaron a esa mesa)' : ''}`
      : (from ? `De ${from} a ${to}` : `Mudados a ${to}`);
    this.setState({ message: {
      type: warning ? 'warning' : 'success',
      text: `${base}.${warning ? ` Ojo: ${warning}.` : ''}`,
    } });
    this.refreshAll();
    return result;
  };

  // ENI-197: cobrar y liberar en una sola acción. El liberado sólo se encadena si el cobro
  // dejó la cuenta REALMENTE en cero, mirando la respuesta del backend y no lo que el front
  // creía que iba a pasar. Si falla, el cobro igual quedó hecho: se avisa y la mesa sigue
  // ocupada, que es recuperable — al revés no.
  onSettleAccount = async (reservationId, payload, { release = false } = {}) => {
    const result = await settleTableAccount(reservationId, payload);
    const pending = result && result.account && result.account.totals
      ? Number(result.account.totals.pending) : null;

    if (release && pending === 0) {
      try {
        await releaseTable(reservationId);
        this.setState({ message: { type: 'success', text: 'Cobro registrado y mesa liberada.' } });
        this.refreshAll();
        return { ...result, released: true };
      } catch (e) {
        this.setState({ message: { type: 'danger', text: `Cobro registrado, pero no se pudo liberar la mesa: ${e.message}` } });
        this.refreshAll();
        return { ...result, released: false, releaseError: e.message };
      }
    }

    this.setState({ message: { type: 'success', text: 'Cobro registrado.' } });
    this.refreshAll();
    return result;
  };

  // Eventos con consumo incluido (FR-098): pasar a incluido una comanda que se cargó a la mesa
  // por error. Cambia la deuda de la mesa y el contador del evento, que la agenda repite en
  // todas sus mesas: se refresca todo.
  onIncludeOrder = async (purchaseId) => {
    const result = await includeOrder(purchaseId);
    this.setState({ message: { type: 'success', text: 'Comanda pasada a incluido: ya no se cobra.' } });
    this.refreshAll();
    return result;
  };

  renderSalon() {
    return (
      <SalonView
        agenda={this.state.agenda}
        agendaLoaded={this.state.agendaLoaded}
        schedule={this.state.scheduleForm}
        demoGames={this.state.demoGames}
        onSeat={this.onSeat}
        onCheckIn={this.checkIn}
        onStartActivity={this.onStartActivity}
        onRelease={this.release}
        onOpenDetail={this.openDetail}
        lookupClient={this.lookupClient}
        onAddGame={this.onAddGame}
        onRemoveGame={this.onRemoveGame}
        onLoadAccount={this.onLoadAccount}
        onSettleAccount={this.onSettleAccount}
        onIncludeOrder={this.onIncludeOrder}
        onLoadMenu={getCafeteriaMenu}
        onAddSeats={this.onAddSeats}
        onRemoveSeats={this.onRemoveSeats}
        onMove={this.onMove}
        onGoPos={(reservationId) => {
          // Dejar la mesa preseleccionada en el POS (se consume una sola vez al montar).
          if (reservationId) localStorage.setItem('posPreselectReservation', reservationId);
          navigateTo('/cafeteria');
        }}
        onRefresh={this.loadAgenda}
      />
    );
  }

  // ---------------------------------------------------------------- Listado
  loadList = async () => {
    const reqId = ++this._listReqId;
    this.setState({ listLoaded: false });
    try {
      const data = await listReservations(this.state.listDate, this.state.listStatus);
      if (reqId !== this._listReqId) return; // descartar respuesta obsoleta
      this.setState({ list: (data && data.reservations) || [], listLoaded: true });
    } catch (e) {
      if (reqId !== this._listReqId) return;
      this.setState({ listLoaded: true, message: { type: 'danger', text: e.message } });
    }
  };

  goToList = () => this.setState({ tab: 'listado' }, () => { if (!this.state.listLoaded) this.loadList(); });

  setListStatus = (key) => this.setState({ listStatus: key }, this.loadList);
  setListDate = (e) => this.setState({ listDate: e.target.value }, this.loadList);
  clearListDate = () => this.setState({ listDate: '' }, this.loadList);

  release = async (id) => {
    if (!window.confirm('¿Liberar la mesa? La reserva quedará completada.')) return;
    try {
      await releaseTable(id);
      this.setState({ message: { type: 'success', text: 'Mesa liberada.' } });
      this.refreshAll();
    } catch (e) {
      this.setState({ message: { type: 'danger', text: e.message } });
    }
  };

  renderListado() {
    const { list, listLoaded, listDate } = this.state;
    return (
      <div className="card-body p-0">
        <div className="d-flex flex-wrap align-items-center px-3 pt-3">
          <div className="mr-3 mb-2">
            <label className="d-block mb-1"><small>Fecha</small></label>
            <div className="d-flex align-items-center">
              <input type="date" className="form-control form-control-sm" style={{ width: 160 }} value={listDate}
                onChange={this.setListDate} />
              {listDate && (
                <button type="button" className="btn btn-sm btn-white ml-1" onClick={this.clearListDate}
                  title="Volver a ver de hoy en adelante">Limpiar</button>
              )}
            </div>
          </div>
          <div className="mb-2">
            <label className="d-block mb-1"><small>Estado</small></label>
            <ul className="nav nav-pills">
              {STATUS_FILTERS.map((f) => (
                <li className="nav-item" key={f.key || 'all'}>
                  <button type="button"
                    className={`btn btn-sm mr-1 ${this.state.listStatus === f.key ? 'btn-primary' : 'btn-white'}`}
                    onClick={() => this.setListStatus(f.key)}>
                    {f.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="px-3 mb-2">
          <small className="text-muted">
            {listDate
              ? `Mostrando sólo el ${fmtYMD(listDate)}.`
              : 'Mostrando de hoy en adelante, las más próximas primero. Elegí una fecha para ver un día puntual.'}
          </small>
        </p>

        {!listLoaded ? (
          <p className="px-3 pb-3">Cargando…</p>
        ) : list.length === 0 ? (
          <p className="px-3 pb-3">
            {listDate ? 'No hay reservas para ese filtro.' : 'No hay reservas de hoy en adelante para ese filtro.'}
          </p>
        ) : (
          <table className="table mb-0">
            <thead className="bg-light">
              <tr>
                <th className="border-0">Fecha</th>
                <th className="border-0">Hora</th>
                <th className="border-0">Mesa</th>
                <th className="border-0 text-center">Personas</th>
                <th className="border-0">Cliente</th>
                <th className="border-0">Juego</th>
                <th className="border-0 text-right">Monto</th>
                <th className="border-0">Entradas</th>
                <th className="border-0">Método</th>
                <th className="border-0">Estado</th>
                <th className="border-0">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fcfcfc' }}>
                  <td>{fmtYMD(r.date)}</td>
                  <td>{pad(r.start_hour)}:00</td>
                  <td>{r.table ? r.table.label : (r.table_id || '—')}</td>
                  <td className="text-center">{r.player_count}</td>
                  {/* FR-011/FR-075: ni la reserva abierta ni la mesa de una jornada tienen
                      titular — se identifican por lo que son, con su etiqueta legible, no con
                      un guión que parece un dato roto. */}
                  <td>{managedBy(r) === 'abierta'
                    ? <span className="badge badge-info">{RESERVATION_ORIGIN_LABEL.abierta}</span>
                    : managedBy(r) === 'actividad'
                      ? <ActivityBadge reservation={r} />
                      : (r.name || '—')}</td>
                  <td>{r.game_name || '—'}</td>
                  <td className="text-right">{Number(r.cover_free) === 1 ? 'Gratis' : (r.total_amount != null ? `$${r.total_amount}` : '—')}</td>
                  <td><PaymentBadge reservation={r} /></td>
                  <td>{PAYMENT_METHOD_LABEL[r.payment_method] || '—'}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button type="button" className="btn btn-primary" onClick={() => this.openDetail(r.id)}>Ver</button>
                      {/* R-04: la reserva de una mesa abierta NO se da de baja por acá. Cancelarla
                          desde el flujo genérico dejaría la mesa publicada y vendible (sin avisos a
                          los anotados ni "por devolver"), y el backend lo rechaza mientras no se
                          esté jugando: el camino es su propia pantalla. Ojo, el cierre de la noche
                          de una mesa YA SENTADA sí se libera, pero desde el panel del salón. */}
                      {managedBy(r) === 'abierta' && (
                        <button type="button" className="btn btn-white" title="La mesa abierta se cancela desde su pantalla"
                          onClick={() => navigateTo('/openTables')}>Mesas abiertas</button>
                      )}
                      {/* FR-075: lo mismo del otro lado — la mesa de una jornada se quita desde
                          la jornada (ahí vuelve el cupo y se actualiza el plan). Liberar sólo
                          se ofrece cuando el backend lo acepta: la mesa ya sentada, o la
                          jornada ya arrancada (la hija de ayer que nadie cerró). El atajo
                          necesita el adjunto: sin él no sabemos a qué evento ir. */}
                      {managedBy(r) === 'actividad' && (
                        <>
                          {r.activity && r.activity.eventId && (
                            <button type="button" className="btn btn-white" title="La mesa se quita desde la jornada"
                              onClick={() => navigateTo(`/editEvent?id=${r.activity.eventId}`)}>Ir a la jornada</button>
                          )}
                          {(r.status === 'seated' || (r.activity && r.activity.hasStarted)) && (
                            <button type="button" className="btn btn-white" onClick={() => this.release(r.id)}>Liberar</button>
                          )}
                        </>
                      )}
                      {managedBy(r) === null && (r.status === 'active' || r.status === 'pending') && (
                        <button type="button" className="btn btn-white" onClick={() => this.release(r.id)}>Liberar</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------- Detalle
  openDetail = async (id) => {
    const reqId = ++this._detailReqId;
    this.setState({
      detail: { id }, detailLoaded: false, newGuest: { name: '', cedula: '' },
      detailMove: { open: false, tableId: '', count: 0, busy: false, error: null, note: null },
      detailReschedule: emptyDetailReschedule(),
      detailPayment: emptyDetailPayment(),
    });
    try {
      const data = await getReservationDetail(id);
      if (reqId !== this._detailReqId) return; // respuesta obsoleta o modal cerrado/cambiado
      const reservation = data && data.reservation;
      if (!reservation) {
        this.setState({ detailLoaded: true, message: { type: 'danger', text: 'No se pudo cargar la reserva.' } });
        return;
      }
      this.setState({ detail: reservation, detailLoaded: true });
    } catch (e) {
      if (reqId !== this._detailReqId) return;
      this.setState({ detail: null, message: { type: 'danger', text: e.message } });
    }
  };

  closeDetail = () => { this._detailReqId++; this.setState({ detail: null, detailLoaded: false }); };

  // ENI-264: cambiar la mesa desde el detalle. El caso que lo motivó: la mesa de una reserva
  // futura la ocupó un walk-in ("reservada a las 19, pero a las 17 se sentó otra gente") y hay
  // que reubicar la reserva sin pasar por el plano. Reusa onMove (mismo endpoint que el salón).
  toggleDetailMove = () => {
    const { open } = this.state.detailMove;
    // La agenda alimenta el select de destino y las etiquetas de merge, pero el polling sólo
    // corre en la pestaña salón: acá puede estar vieja de HORAS. Se repide SIEMPRE al abrir.
    if (!open) this.loadAgenda();
    this.setState({ detailMove: { open: !open, tableId: '', count: 0, busy: false, error: null, note: null } });
  };

  submitDetailMove = async () => {
    const r = this.state.detail;
    const tableId = Number(this.state.detailMove.tableId);
    const count = Number(this.state.detailMove.count) || 0; // 0 = toda la mesa
    if (!r || !tableId || this.state.detailMove.busy) return;
    // Mismo guard que en el salón: un count viejo >= player_count escala a mover la mesa entera.
    const players = Number(r.player_count) || 0;
    if (count > 0 && count >= players) {
      this.setState({ detailMove: { ...this.state.detailMove, count: 0, error: 'La mesa cambió: elegí de nuevo cuántos se mudan' } });
      return;
    }
    // Last-wins como en openDetail: si el operador cerró el modal (o abrió otra reserva)
    // mientras el request volaba, la continuación NO debe reabrirle el modal viejo encima.
    const reqIdAtSubmit = this._detailReqId;
    this.setState({ detailMove: { ...this.state.detailMove, busy: true, error: null } });
    try {
      const result = await this.onMove(r.id, tableId, count > 0 ? count : undefined);
      if (this._detailReqId !== reqIdAtSubmit) return; // el listado igual se refrescó vía onMove
      await this.openDetail(r.id); // recarga el detalle (y resetea el bloque); Mesa ya muestra la nueva
      const warning = result && result.warning;
      if (warning) this.setState({ detailMove: { ...this.state.detailMove, note: `Ojo: ${warning}.` } });
    } catch (e) {
      if (this._detailReqId !== reqIdAtSubmit) return;
      this.setState({ detailMove: { ...this.state.detailMove, busy: false, error: e.message || 'No se pudo cambiar de mesa' } });
    }
  };

  /**
   * Cobrar las entradas de una reserva desde el detalle del listado.
   *
   * El importe NO se calcula acá: se pide la cuenta al backend (`getTableAccount`), que devuelve
   * el pendiente EXACTO. `total_amount` acumula a los precios de cada momento (ENI-196), así que
   * personas × cover daría otro número que el que se termina cobrando.
   */
  toggleDetailPayment = async () => {
    const r = this.state.detail;
    if (!r) return;
    if (this.state.detailPayment.open) { this.setState({ detailPayment: emptyDetailPayment() }); return; }

    // El medio DECLARADO al reservar es el default más probable (quien pidió los datos de
    // transferencia transfirió), pero se puede cambiar: el ticket lleva el que se elige acá.
    const payType = PAYMENT_METHOD_TO_TYPE[r.payment_method] || 2;
    this.setState({ detailPayment: { ...emptyDetailPayment(), open: true, loading: true, payType } });
    const reqIdAtOpen = this._detailReqId;
    try {
      // Los lectores no pueden tumbar el panel: sin ellos el cobro funciona igual (el backend
      // resuelve `posDeviceId` a null), sólo se pierde por cuál aparato entró.
      const [account, devices] = await Promise.all([
        this.onLoadAccount(r.id),
        getPosDevices().catch(() => []),
      ]);
      if (this._detailReqId !== reqIdAtOpen) return; // el operador cerró el modal o abrió otra reserva
      const preferido = pickDefaultPosDevice(devices);
      this.setState({ detailPayment: {
        ...this.state.detailPayment, loading: false, account,
        posDevices: preferido ? devices : [],
        posDeviceId: preferido ? String(preferido.id) : '',
      } });
    } catch (e) {
      if (this._detailReqId !== reqIdAtOpen) return;
      this.setState({ detailPayment: { ...this.state.detailPayment, loading: false, error: e.message || 'No se pudo leer la cuenta' } });
    }
  };

  // Saldo a favor del titular, tal como vino en el estado de cuenta (el cliente ya está
  // identificado por la reserva: no hay cédula que pedir).
  detailCreditBalance = () => {
    const account = this.state.detailPayment.account;
    return account && account.customerCedula ? Number(account.storeCredit || 0) : 0;
  };

  // Se usa todo lo que alcance de lo que se está cobrando (las entradas pendientes).
  detailAppliedCredit = (amount) => (this.state.detailPayment.payWithCredit
    ? Math.min(this.detailCreditBalance(), Number(amount) || 0)
    : 0);

  detailCreditCoversAll = (amount) => (Number(amount) > 0 && this.detailAppliedCredit(amount) >= Number(amount));

  onChangeDetailPayment = (value) => {
    const next = value === 'credit'
      ? { payWithCredit: true }
      : { payWithCredit: false, payType: Number(value) };
    this.setState({ detailPayment: { ...this.state.detailPayment, ...next } });
  };

  submitDetailPayment = async () => {
    const r = this.state.detail;
    const { account, busy, payType, posDeviceId } = this.state.detailPayment;
    if (!r || busy || !account) return;
    // Sólo las ENTRADAS. Las comandas se cobran desde el salón, con la mesa a la vista: acá la
    // reserva todavía no llegó y meterlas en el mismo botón cobraría consumo sin querer.
    const seats = Number(account.seats && account.seats.pending) || 0;
    if (seats <= 0) {
      this.setState({ detailPayment: { ...this.state.detailPayment, error: 'No quedan entradas por cobrar' } });
      return;
    }
    const reqIdAtSubmit = this._detailReqId;
    this.setState({ detailPayment: { ...this.state.detailPayment, busy: true, error: null } });
    try {
      // Saldo a favor: se descuenta de las entradas que se cobran acá; si cubre todo, el ticket
      // se emite en EFECTIVO (uno de $0 en transferencia le pediría al cajero un comprobante que
      // no existe). Mismo criterio que el salón y el POS.
      const amount = Number(account.seats && account.seats.pendingAmount) || 0;
      const credit = this.detailAppliedCredit(amount);
      const effectiveType = this.detailCreditCoversAll(amount) ? 1 : Number(payType);
      // Con POS va el lector: el cierre de lote se concilia por aparato, y un cobro sin aparato
      // es un renglón que no cuadra contra ninguno (misma regla que el cobro del salón).
      await this.onSettleAccount(r.id, {
        seats,
        paymentType: effectiveType,
        ...(effectiveType === 5 && posDeviceId ? { posDeviceId: Number(posDeviceId) } : {}),
        ...(credit > 0 ? { creditApplied: credit } : {}),
      });
      if (this._detailReqId !== reqIdAtSubmit) return; // el listado ya se refrescó vía onSettleAccount
      await this.openDetail(r.id); // recarga el detalle: el estado de cobro pasa a "Paga"
    } catch (e) {
      if (this._detailReqId !== reqIdAtSubmit) return;
      const message = e.message || 'No se pudo registrar el pago';
      // El rechazo típico es de carrera ("Sólo quedan N lugares sin pagar"): dejar el importe
      // viejo en pantalla al lado de ese mensaje es pedirle al operador que resuelva él la
      // contradicción. Se relee la cuenta; si eso también falla, al menos queda el motivo.
      let account = this.state.detailPayment.account;
      try { account = await this.onLoadAccount(r.id); } catch (reloadError) { /* se conserva la vieja */ }
      if (this._detailReqId !== reqIdAtSubmit) return;
      this.setState({ detailPayment: { ...this.state.detailPayment, busy: false, error: message, account } });
    }
  };

  changeStatus = async (e) => {
    const status = e.target.value;
    if (!status || !this.state.detail || this.state.statusBusy) return;
    const id = this.state.detail.id;
    this.setState({ statusBusy: true });
    try {
      await patchReservationStatus(id, status);
      this.refreshAll();
      await this.openDetail(id);
      this.setState({ statusBusy: false, message: { type: 'success', text: 'Estado actualizado.' } });
    } catch (err) {
      this.setState({ statusBusy: false, message: { type: 'danger', text: err.message } });
    }
  };

  addGuestRow = async () => {
    const { name, cedula } = this.state.newGuest;
    if ((!name && !cedula) || this.state.guestBusy || !this.state.detail) return;
    const id = this.state.detail.id;
    this.setState({ guestBusy: true });
    try {
      await addGuests(id, [{ name: name || null, cedula: cedula || null }]);
      this.setState({ newGuest: { name: '', cedula: '' }, guestBusy: false });
      this.openDetail(id);
    } catch (e) {
      this.setState({ guestBusy: false, message: { type: 'danger', text: e.message } });
    }
  };

  removeGuestRow = async (guestId) => {
    if (this.state.guestBusy || !this.state.detail) return;
    const id = this.state.detail.id;
    this.setState({ guestBusy: true });
    try {
      await removeGuest(id, guestId);
      this.setState({ guestBusy: false });
      this.openDetail(id);
    } catch (e) {
      this.setState({ guestBusy: false, message: { type: 'danger', text: e.message } });
    }
  };

  // Bloque "cambiar de mesa" del detalle. A diferencia del salón NO se filtra por "libre ahora":
  // la reserva puede ser para más tarde u otro día, así que "ocupada en este momento" no dice
  // nada. Se ofrecen todas las mesas activas y la ocupación a la hora que corresponde la valida
  // el backend (moveReservationToTable); su error se muestra acá mismo.
  renderDetailMove(r) {
    const { open, tableId, count, busy, error, note } = this.state.detailMove;
    if (note) return <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: 12 }}>{note}</div>;
    if (!open) return null;
    const players = Number(r.player_count) || 0;
    // ENI-269: mover PARTE sólo desde una mesa sentada (redistribución en vivo). El count se
    // clampea contra el player_count vivo (submitDetailMove lo re-chequea igual).
    const canSplit = r.status === 'seated' && players > 1;
    const clampedCount = Math.min(Number(count) || 0, Math.max(0, players - 1));
    const partial = canSplit && clampedCount > 0;
    // Quién está sentado en cada mesa HOY, para etiquetar el merge (y filtrar gratis/paga).
    const seatedByTable = {};
    (this.state.agenda.reservations || [])
      .filter((x) => x.status === 'seated' && x.id !== r.id)
      .forEach((x) => { seatedByTable[x.table_id] = x; });
    // FR-070 — el merge de una hija sólo con sus hermanas, y una hija nunca es destino de una
    // reserva ajena: fundirlas dejaría una sola fila mitad jornada y mitad salón. El corte va
    // por `activity_id` (la columna), no por el adjunto, que es best-effort.
    const activityId = r.activity_id == null ? null : Number(r.activity_id);
    const tables = (this.state.agenda.tables || [])
      .filter((t) => Number(t.id) !== Number(r.table_id))
      // En un parcial no se ofrece mezclar una mesa gratis con una paga (el backend lo rechaza).
      .filter((t) => {
        if (!partial) return true;
        const dest = seatedByTable[t.id];
        if (!dest) return true;
        const destActivityId = dest.activity_id == null ? null : Number(dest.activity_id);
        if (destActivityId !== activityId) return false;
        return Number(dest.cover_free || 0) === Number(r.cover_free || 0);
      });
    return (
      <div className="mb-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        {canSplit && (
          <div className="mb-2">
            <label className="d-block mb-1"><small>¿Quiénes se mudan?</small></label>
            <select className="form-control form-control-sm" value={clampedCount}
              onChange={(e) => this.setState({ detailMove: { ...this.state.detailMove, count: Number(e.target.value), tableId: '' } })}>
              <option value={0}>Toda la mesa ({players} personas)</option>
              {Array.from({ length: players - 1 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n} persona{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        )}
        <label className="d-block mb-1"><small>Se mudan a…</small></label>
        {tables.length === 0 ? (
          <p className="text-muted mb-2" style={{ fontSize: 12 }}>
            {this.state.agendaLoaded ? 'No hay otra mesa para ofrecer.' : 'Cargando mesas…'}
          </p>
        ) : (
          <select className="form-control form-control-sm mb-2" value={tableId}
            onChange={(e) => this.setState({ detailMove: { ...this.state.detailMove, tableId: e.target.value } })}>
            <option value="">Elegí la mesa</option>
            {tables.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.capacity} personas{t.is_vip ? ' · VIP' : ''}
                {partial && seatedByTable[t.id] ? ` · con ${seatedByTable[t.id].name || 'gente'} (${seatedByTable[t.id].player_count}p) — se suman ahí` : ''}
              </option>
            ))}
          </select>
        )}
        <p className="text-muted mb-2" style={{ fontSize: 11 }}>
          {partial
            ? 'Se llevan su parte de la cuenta: las entradas impagas se cobran en la mesa nueva. Comandas y juegos quedan acá.'
            : 'Se lleva todo: entradas, consumos y juegos. Si la mesa está ocupada a esa hora, se avisa acá.'}
        </p>
        {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
        <button type="button" className="btn btn-accent btn-sm btn-block" disabled={busy || !tableId}
          onClick={this.submitDetailMove}>
          {partial ? `Mover ${clampedCount} persona${clampedCount > 1 ? 's' : ''}` : 'Mover'}
        </button>
      </div>
    );
  }

  /**
   * Cambiar la FECHA y/o la HORA de una reserva que todavía no llegó.
   *
   * El caso real: el cliente llama y avisa que llega más tarde, o pide pasar la reserva a otro
   * día. Hasta ahora la única salida era cancelar y cargarla de nuevo — perdiendo el cobro
   * anticipado, los invitados y el juego elegido.
   *
   * Qué mesa queda NO se adivina acá (mismo criterio que el bloque de mover): el backend
   * conserva la que tiene si sigue libre a esa hora y, si no, reasigna una y dice cuál.
   */
  toggleDetailReschedule = async () => {
    const r = this.state.detail;
    if (!r) return;
    if (this.state.detailReschedule.open) { this.setState({ detailReschedule: emptyDetailReschedule() }); return; }
    // El combo de horas sale del horario configurado (con sus overrides por día). El schedule
    // se carga en la pestaña de configuración, así que desde el listado puede no estar todavía.
    if (!this.state.scheduleForm) {
      try {
        const data = await getSchedule();
        if (data && data.schedule) this.setState({ scheduleForm: data.schedule });
      } catch (e) { /* el form cae a los defaults (14–21) */ }
    }
    this.setState({
      detailReschedule: {
        ...emptyDetailReschedule(),
        open:      true,
        date:      String(r.date || '').slice(0, 10),
        startHour: Number(r.start_hour) || 0,
      },
    });
  };

  // Cambiar de día puede dejar la hora elegida fuera del horario de ESE día (un viernes abre
  // hasta más tarde que un lunes): ahí se cae a la apertura, como hace el walk-in. Sin esto el
  // select queda en blanco y el submit manda una hora que el backend rebota.
  setDetailReschedule = (patch) => {
    const next = { ...this.state.detailReschedule, ...patch, error: null };
    const day = scheduleForDate(this.state.scheduleForm || {}, next.date);
    const open = day.openHour ?? 14;
    const last = day.lastReservation ?? 21;
    if (!(Number(next.startHour) >= open && Number(next.startHour) <= last)) next.startHour = open;
    this.setState({ detailReschedule: next });
  };

  submitDetailReschedule = async () => {
    const r = this.state.detail;
    const { date, startHour, busy } = this.state.detailReschedule;
    if (!r || busy || !date) return;
    // Last-wins como en el resto del detalle: si el operador cerró el modal (o abrió otra
    // reserva) mientras el request volaba, la continuación no debe reabrirle el modal viejo.
    const reqIdAtSubmit = this._detailReqId;
    this.setState({ detailReschedule: { ...this.state.detailReschedule, busy: true, error: null } });
    try {
      const result = await rescheduleReservation(r.id, date, Number(startHour));
      if (this._detailReqId !== reqIdAtSubmit) return;
      // El listado va ordenado por fecha y hora: la fila se muda de lugar (o de día).
      if (this.state.listLoaded) await this.loadList();
      if (this.state.tab === 'salon') this.loadAgenda();
      await this.openDetail(r.id); // recarga el detalle (y cierra este bloque)

      const extras = [];
      if (result && result.tableChanged && result.table) extras.push(`la mesa pasó a ser ${result.table.label}`);
      if (result && result.warning) extras.push(String(result.warning).toLowerCase());
      this.setState({
        message: {
          type: 'success',
          text: `Reserva reprogramada al ${fmtYMD(date)} a las ${pad(startHour)}:00${extras.length ? ` — ${extras.join('; ')}` : ''}.`,
        },
      });
    } catch (e) {
      if (this._detailReqId !== reqIdAtSubmit) return;
      this.setState({ detailReschedule: { ...this.state.detailReschedule, busy: false, error: e.message || 'No se pudo cambiar el horario' } });
    }
  };

  // Bloque "cambiar horario" del detalle. No filtra nada por disponibilidad: qué mesa queda
  // libre a esa hora lo resuelve el backend con lock, y su respuesta (o su rechazo) se muestra
  // acá mismo — igual que el bloque de mover.
  renderDetailReschedule(r) {
    const { open, date, startHour, busy, error } = this.state.detailReschedule;
    if (!open) return null;
    const day = scheduleForDate(this.state.scheduleForm || {}, date);
    const openHour = day.openHour ?? 14;
    const last = day.lastReservation ?? 21;
    const hours = [];
    for (let h = openHour; h <= last; h++) hours.push(h);
    const sinCambio = String(date) === String(r.date || '').slice(0, 10)
      && Number(startHour) === Number(r.start_hour);

    return (
      <div className="mb-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        <div className="form-row">
          <div className="form-group col-6 mb-2">
            <label className="d-block mb-1"><small>Fecha nueva</small></label>
            <input type="date" className="form-control form-control-sm" value={date}
              onChange={(e) => this.setDetailReschedule({ date: e.target.value })} />
          </div>
          <div className="form-group col-6 mb-2">
            <label className="d-block mb-1"><small>Hora nueva</small></label>
            <select className="form-control form-control-sm" value={startHour}
              onChange={(e) => this.setDetailReschedule({ startHour: Number(e.target.value) })}>
              {hours.map((h) => <option key={h} value={h}>{pad(h)}:00</option>)}
            </select>
          </div>
        </div>
        <p className="text-muted mb-2" style={{ fontSize: 11 }}>
          Se conserva la mesa {r.table ? r.table.label : 'asignada'} si sigue libre a esa hora; si no, se reasigna
          otra y se avisa cuál. La cuenta no se toca: lo ya cobrado sigue cobrado.
        </p>
        {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
        <button type="button" className="btn btn-accent btn-sm btn-block" disabled={busy || !date || sinCambio}
          onClick={this.submitDetailReschedule}>
          {busy ? 'Cambiando…' : 'Cambiar horario'}
        </button>
      </div>
    );
  }

  /**
   * Registrar el pago de las entradas de una reserva que todavía no llegó (o que ya está sentada
   * pero se pagó afuera). Cobra SÓLO las entradas y siempre TODAS las que falten: el caso real es
   * "transfirió el total antes de venir", y un cobro parcial acá dejaría media cuenta abierta
   * sin nadie enfrente a quien preguntarle. El cobro parcial vive en el salón, con la mesa a la
   * vista ("Cobrar parte").
   */
  renderDetailPayment(r) {
    const { open, account, loading, busy, error, payType, posDevices, posDeviceId, payWithCredit } = this.state.detailPayment;
    if (!open) return null;

    const seats  = account ? (Number(account.seats && account.seats.pending) || 0) : 0;
    const amount = account ? (Number(account.seats && account.seats.pendingAmount) || 0) : 0;
    // Comandas impagas: no entran en este cobro. Si existen (mesa ya sentada), se avisa para que
    // nadie crea que el botón dejó la cuenta en cero.
    const pendingOrders = account ? (account.orders || []).filter((o) => !o.paid) : [];
    const ordersAmount  = pendingOrders.reduce((acc, o) => acc + (Number(o.amount) || 0), 0);

    return (
      <div className="mb-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        {loading ? (
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>Cargando la cuenta…</p>
        ) : !account ? (
          <div className="alert alert-danger py-1 px-2 mb-0" style={{ fontSize: 12 }}>
            {error || 'No se pudo leer la cuenta de la mesa.'}
          </div>
        ) : seats === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>No quedan entradas por cobrar en esta reserva.</p>
        ) : (
          <div>
            <div className="mb-2">
              <strong>{seats} entrada{seats > 1 ? 's' : ''} sin cobrar · ${amount}</strong>
            </div>
            <label className="d-block mb-1"><small>¿Con qué pagó?</small></label>
            {/* El saldo a favor del titular es un medio más y sólo aparece si tiene: el cliente
                ya está identificado por la reserva, no hay cédula que pedir. */}
            <select className="form-control form-control-sm mb-2" value={payWithCredit ? 'credit' : payType} disabled={busy}
              onChange={(e) => this.onChangeDetailPayment(e.target.value)}>
              {SETTLE_PAYMENTS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              {this.detailCreditBalance() > 0 && (
                <option value="credit">Saldo a favor (${this.detailCreditBalance()})</option>
              )}
            </select>
            {/* Si el saldo no cubre las entradas, el resto se cobra con otro medio, elegido acá
                mismo: se cobra todo junto en una sola operación. */}
            {payWithCredit && !this.detailCreditCoversAll(amount) && (
              <React.Fragment>
                <label className="d-block mb-1">
                  <small>El saldo no alcanza — cobrar ${amount - this.detailAppliedCredit(amount)} con:</small>
                </label>
                <select className="form-control form-control-sm mb-2" value={payType} disabled={busy}
                  onChange={(e) => this.setState({ detailPayment: { ...this.state.detailPayment, payType: Number(e.target.value) } })}>
                  {SETTLE_PAYMENTS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </React.Fragment>
            )}
            {this.detailAppliedCredit(amount) > 0 && (
              <p className="mb-2" style={{ fontSize: 12, color: '#1c7a48' }}>
                Saldo a favor −${this.detailAppliedCredit(amount)}
                {this.detailCreditCoversAll(amount)
                  ? ' · lo cubre entero'
                  : ` · a cobrar $${amount - this.detailAppliedCredit(amount)}`}
              </p>
            )}
            {/* Mismo criterio que el salón: con POS hay que decir por cuál lector entró, o el
                cierre de lote de ese aparato queda con un renglón que no cuadra. */}
            {Number(payType) === 5 && !this.detailCreditCoversAll(amount) && posDevices.length > 1 && (
              <select className="form-control form-control-sm mb-2" value={posDeviceId} disabled={busy}
                onChange={(e) => this.setState({ detailPayment: { ...this.state.detailPayment, posDeviceId: e.target.value } })}>
                {posDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            {pendingOrders.length > 0 && (
              <p className="text-muted mb-2" style={{ fontSize: 11 }}>
                Esta mesa tiene además {pendingOrders.length} comanda(s) sin cobrar (${ordersAmount}).
                El consumo se cobra desde el salón; acá se registran sólo las entradas.
              </p>
            )}
            <p className="text-muted mb-2" style={{ fontSize: 11 }}>
              Se emite la venta con ticket y entra a la caja de hoy. Las entradas cobradas no se
              devuelven desde el sistema: si al final viene menos gente, la mesa queda cobrada por {seats + (Number(r.paid_seats) || 0)}.
            </p>
            {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{error}</div>}
            <button type="button" className="btn btn-accent btn-sm btn-block" disabled={busy}
              onClick={this.submitDetailPayment}>
              {busy ? 'Registrando…' : `Registrar pago de $${amount - this.detailAppliedCredit(amount)}`}
            </button>
          </div>
        )}
      </div>
    );
  }

  renderDetailModal() {
    const { detail, detailLoaded } = this.state;
    if (!detail) return null;
    const r = detail;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto' }}>
        <div className="card card-small" style={{ maxWidth: 640, width: '92%', marginTop: 60, marginBottom: 60 }}>
          <div className="card-header border-bottom d-flex justify-content-between align-items-center">
            <h6 className="m-0">Detalle de reserva</h6>
            <button type="button" className="btn btn-sm btn-white" onClick={this.closeDetail}>✕</button>
          </div>
          <div className="card-body">
            {!detailLoaded ? (
              <p>Cargando…</p>
            ) : (
              <div>
                <div className="row mb-2">
                  <div className="col-6"><small className="text-muted">Fecha</small><div>{fmtYMD(r.date)}</div></div>
                  <div className="col-6">
                    <small className="text-muted">Hora</small>
                    {/* El botón va acá, pegado al dato que cambia (la fecha y la hora se
                        reprograman juntas), igual que "⇄ mover" vive al lado de la mesa. */}
                    <div className="d-flex align-items-center">
                      <span className="mr-2">{pad(r.start_hour)}:00</span>
                      {canReschedule(r) && (
                        <button type="button" className="btn btn-sm btn-white" style={{ fontSize: 12, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}
                          onClick={this.toggleDetailReschedule}>
                          {this.state.detailReschedule.open ? '✕' : '🕑 cambiar horario'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {this.renderDetailReschedule(r)}
                <div className="row mb-2">
                  <div className="col-6">
                    <small className="text-muted">Mesa</small>
                    <div className="d-flex align-items-center">
                      <span className="mr-2">{r.table ? r.table.label : (r.table_id || '—')}</span>
                      {['active', 'pending', 'seated'].includes(r.status) && (
                        <button type="button" className="btn btn-sm btn-white" style={{ fontSize: 12, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}
                          onClick={this.toggleDetailMove}>
                          {this.state.detailMove.open ? '✕' : '⇄ mover'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-6"><small className="text-muted">Personas</small><div>{r.player_count}</div></div>
                </div>
                {this.renderDetailMove(r)}
                <div className="row mb-2">
                  <div className="col-6"><small className="text-muted">Cliente</small>
                    {/* FR-011/FR-075: en la reserva abierta el "cliente" es la mesa abierta
                        misma (los anotados viven en su pantalla de inscripciones); en una mesa
                        de jornada es la jornada (los anotados viven en Anotados del evento). */}
                    <div>{managedBy(r) === 'abierta'
                      ? <span className="badge badge-info">{RESERVATION_ORIGIN_LABEL.abierta}</span>
                      : managedBy(r) === 'actividad'
                        ? <ActivityBadge reservation={r} />
                        : <>{r.name || '—'}{r.user_cedula ? ` (${r.user_cedula})` : ''}</>}</div>
                  </div>
                  {/* Antes era `email || phone`: como el email siempre viene (la cuenta es de
                      Google), el teléfono no se mostraba NUNCA, ni cuando existía. Son dos
                      datos distintos y el que sirve para llamar el día de la reserva es el
                      móvil, así que va primero y en negrita. */}
                  <div className="col-6">
                    <small className="text-muted">Contacto</small>
                    <div>
                      {r.phone
                        ? <a href={`tel:${String(r.phone).replace(/\s/g, '')}`}><strong>{r.phone}</strong></a>
                        : <span className="text-muted">Sin teléfono</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8125rem' }}>{r.email || 'Sin email'}</div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-6"><small className="text-muted">Juego</small><div>{r.game_name || '—'}</div></div>
                  {/* El "Método" es el medio DECLARADO al reservar, no una prueba de que la
                      plata entró: leído solo, "· Transferencia" parece decir que ya pagó. El
                      badge de abajo es el estado real de cobro, y al lado la puerta para
                      registrarlo cuando el pago se hizo por fuera (el caso típico: transfirió
                      antes de venir, con los datos que el propio backoffice le dio al reservar). */}
                  <div className="col-6">
                    <small className="text-muted">Monto</small>
                    <div>{Number(r.cover_free) === 1 ? 'Gratis' : <span>{r.total_amount != null ? `$${r.total_amount}` : '—'} · {PAYMENT_METHOD_LABEL[r.payment_method] || '—'}</span>}</div>
                    <div className="d-flex align-items-center mt-1">
                      <span className="mr-2"><PaymentBadge reservation={r} /></span>
                      {canChargeSeats(r) && (
                        <button type="button" className="btn btn-sm btn-white" style={{ fontSize: 12, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}
                          onClick={this.toggleDetailPayment}>
                          {this.state.detailPayment.open ? '✕' : 'registrar pago'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {this.renderDetailPayment(r)}

                {/* R-04: en la reserva de una mesa abierta el estado es de sólo lectura. Pasarla
                    a Cancelada/Liberada por acá deja la mesa abierta 'Publicada' y vendible: los
                    anotados no reciben aviso, las inscripciones pagas no quedan "por devolver" y
                    el cliente puede seguir anotándose (y pagando) por el link del detalle. Se
                    cancela desde Mesas abiertas, que sí cascadea. */}
                <div className="row mb-3 align-items-end">
                  <div className="col-6">
                    <small className="text-muted d-block mb-1">Estado</small>
                    {managedBy(r) === 'abierta' ? (
                      <div>
                        <StatusBadge status={r.status} />
                        <small className="text-muted d-block mt-1">
                          La mesa abierta se cancela desde su pantalla (avisa a los anotados y libera el cupo).
                        </small>
                      </div>
                    ) : managedBy(r) === 'actividad' ? (
                      /* FR-075: mismo criterio con la mesa de una jornada. Cancelarla o
                         sentarla desde acá el backend las rechaza (7307): la quita la jornada,
                         que devuelve el cupo, y la sienta el check-in de las entradas. */
                      <div>
                        <StatusBadge status={r.status} />
                        <small className="text-muted d-block mt-1">
                          La mesa la maneja la jornada: se quita desde ahí (devuelve el cupo) y se
                          sienta con el check-in de las entradas.
                        </small>
                      </div>
                    ) : (
                      <select className="form-control form-control-sm" value={r.status} disabled={this.state.statusBusy} onChange={this.changeStatus}>
                        {Object.keys(RESERVATION_STATUS).map((s) => (
                          <option key={s} value={s}>{RESERVATION_STATUS[s].label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  {managedBy(r) === 'abierta' ? (
                    <div className="col-6">
                      <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo('/openTables')}>
                        Ir a Mesas abiertas
                      </button>
                    </div>
                  ) : managedBy(r) === 'actividad' ? (
                    <div className="col-6">
                      {r.activity && r.activity.eventId && (
                        <button type="button" className="btn btn-sm btn-white mr-1"
                          onClick={() => navigateTo(`/editEvent?id=${r.activity.eventId}`)}>Ir a la jornada</button>
                      )}
                      {(r.status === 'seated' || (r.activity && r.activity.hasStarted)) && (
                        <button type="button" className="btn btn-sm btn-white" onClick={() => this.release(r.id)}>Liberar mesa</button>
                      )}
                    </div>
                  ) : (r.status === 'active' || r.status === 'pending') && (
                    <div className="col-6">
                      <button type="button" className="btn btn-sm btn-white" onClick={() => this.release(r.id)}>Liberar mesa</button>
                    </div>
                  )}
                </div>

                <hr />
                {this.renderDetailPeople(r)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Personas de la reserva. Son DOS listas distintas y hasta ahora sólo se veía una:
  //  - ANOTADOS: las inscripciones de la mesa abierta (`open_table_signup`) — la gente que
  //    viene, con sus lugares y su pago. Es la lista que importa en una mesa abierta.
  //  - CLIENTES VINCULADOS: los invitados cargados a mano (`reservation_guests`). Anotarse
  //    NO escribe esa tabla (son conceptos paralelos: el invitado a mano no tiene lugares,
  //    estado ni pago), así que en una mesa abierta la lista está siempre vacía y el detalle
  //    decía "Sin clientes vinculados" con la mesa llena — el defecto que reportó el dueño.
  //    No se duplican filas al anotarse (dos fuentes de verdad que se desincronizan al
  //    cancelar, vencer o mover una inscripción): el detalle lee cada tabla donde vive.
  // En una mesa abierta el bloque de invitados a mano aparece sólo si TIENE filas; en una
  // reserva común esta sección queda exactamente como estaba.
  renderDetailPeople(r) {
    const isOpenTable = managedBy(r) === 'abierta';
    const guests = r.guests || [];
    // `null` = el detalle no trajo el campo (backend viejo): no se puede afirmar "nadie
    // anotado" — sería el mismo engaño de antes con otro texto.
    const signups = Array.isArray(r.openTableSignups) ? r.openTableSignups : null;
    return (
      <>
        {isOpenTable && this.renderSignups(signups)}
        {(!isOpenTable || guests.length > 0) && (
          <>
            <h6 className={isOpenTable ? 'mt-4' : undefined}>Clientes vinculados</h6>
            {guests.length === 0 ? (
              <p className="text-muted">Sin clientes vinculados.</p>
            ) : (
              <ul className="list-group mb-2">
                {guests.map((g) => (
                  <li key={g.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                    <span>{g.name || '—'}{g.cedula ? ` · ${g.cedula}` : ''}{g.account_id ? ` · cuenta #${g.account_id}` : ''}</span>
                    <button type="button" className="btn btn-sm btn-white" disabled={this.state.guestBusy} onClick={() => this.removeGuestRow(g.id)}>✕</button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {/* Vincular a mano sigue siendo válido en una mesa abierta (el amigo que viene con
            el anotado, la cédula que hay que dejar asentada), pero con el aclarador para
            que no se lea como "anotar a alguien a la mesa". */}
        {isOpenTable && guests.length === 0 && (
          <small className="text-muted d-block mt-4 mb-1">Vincular una persona a mano (no la anota a la mesa ni ocupa lugares):</small>
        )}
        <div className="form-row">
          <div className="col">
            <input type="text" className="form-control form-control-sm" placeholder="Nombre"
              value={this.state.newGuest.name}
              onChange={(e) => this.setState({ newGuest: { ...this.state.newGuest, name: e.target.value } })} />
          </div>
          <div className="col">
            <input type="text" className="form-control form-control-sm" placeholder="Cédula"
              value={this.state.newGuest.cedula}
              onChange={(e) => this.setState({ newGuest: { ...this.state.newGuest, cedula: e.target.value } })} />
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-sm btn-primary" disabled={this.state.guestBusy} onClick={this.addGuestRow}>Agregar</button>
          </div>
        </div>
      </>
    );
  }

  // Los anotados de la mesa abierta: nombre, lugares y pago (Paga / Pendiente / Esperando
  // pago online, que es lo que preguntó el dueño), más el estado cuando dice algo MÁS que
  // el pago ("No vino"). Mismo vocabulario que la cuenta del salón y la pantalla de
  // inscripciones.
  renderSignups(signups) {
    if (signups === null) {
      return (
        <>
          <h6>Anotados</h6>
          <p className="text-muted">Se ven en Mesas abiertas → la mesa → Inscripciones.</p>
        </>
      );
    }
    // NO existe una suma de inscripciones que cierre con el "Personas" de arriba, así que
    // el resumen no lo promete: dice qué es cada número. `player_count` son los lugares que
    // quedaron en la CUENTA de la mesa, y se mueve por su cuenta en los tres casos:
    //  - un no-show libera cupo pero deja sus lugares (no hay reversa de caja, FR-056),
    //  - un hold de pago online todavía no puso ninguno (entran al confirmarse), y
    //  - el salón suma walk-ins o saca gente sobre la misma reserva sin tocar inscripción
    //    alguna (claimSeatsForReservation / releaseSeatsForReservation).
    // Los dos primeros los rompían al revés (Personas 4 con "2 lugares", Personas 0 con
    // "1 lugar") y el tercero hace que la equivalencia sea imposible, no sólo incorrecta.
    const seatsOf = (list) => list.reduce((acc, s) => acc + (Number(s.seats) || 0), 0);
    const accountSeats = seatsOf(signups.filter((s) => s.status !== 'pending'));
    const holdSeats = seatsOf(signups.filter((s) => s.status === 'pending'));
    return (
      <>
        {/* mb-1 sólo cuando abajo va el resumen de lugares: pegado al título se lee como
            su bajada. Con el texto de "nadie anotado" el margen normal del h6 queda mejor. */}
        <h6 className={signups.length === 0 ? undefined : 'mb-1'}>Anotados</h6>
        {signups.length === 0 ? (
          <p className="text-muted">Nadie anotado todavía.</p>
        ) : (
          <>
            <small className="text-muted d-block mb-2">
              {accountSeats} lugar{accountSeats === 1 ? '' : 'es'} de anotados en la cuenta
              {holdSeats > 0 && ` · ${holdSeats} pagando online (sin lugar todavía)`}
            </small>
            <ul className="list-group mb-2">
              {signups.map((s) => {
                const pay = signupPaymentChip(s);
                const state = signupStatusChip(s);
                const n = Number(s.seats) || 0;
                return (
                  <li key={s.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                    <span>
                      {s.name || 'Sin nombre'}
                      <span className="text-muted"> · {n} lugar{n === 1 ? '' : 'es'}</span>
                      {s.checkedInAt && (
                        <small className="text-success d-block">Llegó {dateFormat(s.checkedInAt, 'HH:MM')}</small>
                      )}
                    </span>
                    <span className="d-flex align-items-center" style={{ gap: 6 }}>
                      {state && <span className={`badge ${state.badge}`}>{state.label}</span>}
                      <span className={`badge ${pay.badge}`}>{pay.label}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </>
    );
  }

  // ---------------------------------------------------------------- Común
  refreshAll = () => {
    this.loadAgenda();
    if (this.state.listLoaded) this.loadList();
  };

  setTab = (tab) => {
    this.setState({ tab, message: null }, () => {
      if (tab === 'listado' && !this.state.listLoaded) this.loadList();
      if (tab === 'salon') this.loadAgenda();
      if (tab === 'bloqueos') this.loadConfig();
    });
  };

  // ---------------------------------------------------------------- Walk-in (F3)
  openWalkin = async () => {
    // Asegurar el horario configurado para clampar/ofrecer horas coherentes con la config.
    let schedule = this.state.scheduleForm;
    if (!schedule) {
      try {
        const data = await getSchedule();
        schedule = data && data.schedule;
        if (schedule) this.setState({ scheduleForm: schedule });
      } catch (e) { /* fallback a defaults abajo */ }
    }
    const open = (schedule && schedule.openHour) || 14;
    const last = (schedule && schedule.lastReservation) || 21;
    const now = new Date();
    const startHour = Math.min(Math.max(now.getHours(), open), last);
    this.setState({
      walkin: emptyWalkinForm(toYMD(now), startHour),
      walkinAvailability: null,
      walkinResult: null,
      walkinClientHint: null,
    });
    if (this.state.demoGames.length === 0) {
      try {
        const data = await getDemoGames();
        this.setState({ demoGames: (data && data.games) || [] });
      } catch (e) { /* opcional: el walk-in funciona sin juego */ }
    }
  };

  closeWalkin = () => this.setState({ walkin: null, walkinAvailability: null, walkinResult: null, walkinClientHint: null });

  setWalkin = (patch) => {
    const walkin = { ...this.state.walkin, ...patch };
    // ENI-198: si cambia la fecha, la hora elegida puede quedar fuera del horario de ESE día
    // (p.ej. venías de un viernes con 23:00 y pasás a un jueves que cierra a las 21). Sin
    // reclampar, el select queda mostrando una opción que ya no existe y el POST se rechaza.
    if (patch.date && patch.date !== this.state.walkin.date) {
      const day = scheduleForDate(this.state.scheduleForm || {}, walkin.date);
      const open = day.openHour ?? 14;
      const last = day.lastReservation ?? 21;
      const current = Number(walkin.startHour);
      if (!(current >= open && current <= last)) walkin.startHour = open;
    }
    this.setState({ walkin, walkinAvailability: null });
  };
  setWalkinCustomer = (patch) => this.setState({ walkin: { ...this.state.walkin, customer: { ...this.state.walkin.customer, ...patch } } });

  // Lookup por cédula (como en el alta de ventas): si el cliente existe, autocompleta nombre y vincula la cuenta.
  lookupWalkinClient = async () => {
    const cedula = (this.state.walkin && this.state.walkin.customer.cedula || '').trim();
    if (!cedula) { this.setState({ walkinClientHint: null }); return; }
    try {
      const data = await getClientByCedula(cedula);
      const p = (data && data.profile) || {};
      const acc = (data && data.account) || {};
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      this.setWalkinCustomer({ name: name || this.state.walkin.customer.name, accountId: acc.id || null });
      this.setState({ walkinClientHint: { type: 'ok', text: name ? `✓ ${name}` : '✓ Cliente encontrado' } });
    } catch (e) {
      this.setState({ walkinClientHint: { type: 'new', text: 'Cliente nuevo' } }); // 4000 = no existe
    }
  };

  // Socio para la reserva: se valida contra su membresía EN LA FECHA de la reserva (no hoy).
  addWalkinMember = async () => {
    const w = this.state.walkin;
    const cedula = String(w.memberCedula || '').replace(/\D/g, '');
    if (!cedula) return;
    if (w.members.some((m) => m.cedula === cedula)) { this.setWalkin({ memberError: 'Esa cédula ya está en la lista.' }); return; }
    try {
      const st = await membershipStatus(cedula, w.date);
      const plan = (st && st.current && st.current.plan_name) || 'su plan';
      if (!st || !st.active) { this.setWalkin({ memberError: `${cedula}: no tiene una membresía vigente ese día. Sumalo como jugador que paga.` }); return; }
      if (!(st.benefits && st.benefits.freeTableToday)) { this.setWalkin({ memberError: `${cedula}: su plan (${plan}) no incluye mesa sin entrada ese día. Sumalo como jugador que paga.` }); return; }
      this.setState({ walkin: { ...this.state.walkin, members: [...w.members, { cedula, plan }], memberCedula: '', memberError: null } });
    } catch (e) {
      this.setWalkin({ memberError: e.message || 'No se pudo consultar la membresía' });
    }
  };
  removeWalkinMember = (cedula) => this.setState({ walkin: { ...this.state.walkin, members: this.state.walkin.members.filter((m) => m.cedula !== cedula) } });

  addWalkinGuest = () => this.setState({ walkin: { ...this.state.walkin, guests: [...this.state.walkin.guests, { name: '', cedula: '' }] } });
  setWalkinGuest = (i, patch) => {
    const guests = this.state.walkin.guests.map((g, idx) => (idx === i ? { ...g, ...patch } : g));
    this.setState({ walkin: { ...this.state.walkin, guests } });
  };
  removeWalkinGuest = (i) => this.setState({ walkin: { ...this.state.walkin, guests: this.state.walkin.guests.filter((_, idx) => idx !== i) } });

  checkWalkinAvailability = async () => {
    const { date, startHour, playerCount } = this.state.walkin;
    try {
      const a = await checkAvailability(date, startHour, playerCount);
      this.setState({ walkinAvailability: a });
    } catch (e) {
      this.setState({ message: { type: 'danger', text: e.message } });
    }
  };

  submitWalkin = async () => {
    const w = this.state.walkin;
    const payload = {
      date: w.date,
      startHour: Number(w.startHour),
      playerCount: Number(w.playerCount),
      paymentMethod: w.paymentMethod,
      gameCode: w.gameCode || null,
      tableId: w.tableId || null,
      customer: (w.customer.name || w.customer.cedula) ? w.customer : null,
      guests: w.guests.filter((g) => g.name || g.cedula),
      // Sin esto el backend cobra siempre: emitía la venta de las entradas al crear la
      // reserva, aunque fuera para el sábado y nadie hubiera pagado todavía.
      deferCover: !!w.deferCover,
      coverFree: !!w.coverFree,
      memberCedulas: w.members.map((m) => m.cedula),
    };
    this.setState({ walkinBusy: true });
    try {
      const result = await createWalkIn(payload);
      // `deferred` sale de lo que se mandó, no de la respuesta: createReservationSale actualiza
      // la reserva por UPDATE y el objeto que vuelve queda con purchase_id/paid_seats viejos.
      this.setState({ walkinBusy: false, walkinResult: { ...result, deferred: payload.deferCover } });
      this.refreshAll();
    } catch (e) {
      this.setState({ walkinBusy: false, message: { type: 'danger', text: e.message } });
    }
  };

  // ---------------------------------------------------------------- Configuración (F4)
  loadConfig = async () => {
    try {
      if (!this.state.scheduleForm) {
        const data = await getSchedule();
        this.setState({ scheduleForm: data && data.schedule ? data.schedule : null });
      }
      this.loadBlocks();
    } catch (e) {
      this.setState({ message: { type: 'danger', text: e.message } });
    }
  };

  loadBlocks = async () => {
    this.setState({ blocksLoaded: false });
    const from = toYMD(new Date());
    const to = toYMD(addDays(new Date(), 60));
    try {
      const data = await getBlocks(from, to);
      this.setState({ blocks: (data && data.blocks) || [], blocksLoaded: true });
    } catch (e) {
      this.setState({ blocksLoaded: true, message: { type: 'danger', text: e.message } });
    }
  };


  setBlockForm = (patch) => this.setState({ blockForm: { ...this.state.blockForm, ...patch } });

  // ENI-221: "Día completo" se manda como bandera y el rango lo resuelve el backend (día
  // calendario 00–24). Antes se calculaba acá copiando el horario del local, así que un bloqueo
  // "todo el día" en realidad sólo cubría openHour..closeHour: quedaba libre todo lo de afuera
  // —alcanzable al sentar gente en el acto, que saltea el gate de horario— e ignoraba el horario
  // especial del día. Además quedaba congelado si después se cambiaba el horario del local.
  submitBlock = async () => {
    if (this.state.blockBusy) return;
    const b = this.state.blockForm;
    const payload = {
      date: b.date,
      fullDay: !!b.fullDay,
      startHour: b.fullDay ? undefined : Number(b.startHour),
      endHour: b.fullDay ? undefined : Number(b.endHour),
      tableId: b.tableId || null,
      description: b.description || null,
    };
    const editandoId = this.state.editingBlockId;
    this.setState({ blockBusy: true });
    try {
      if (editandoId != null) {
        await updateBlock(editandoId, payload);
      } else {
        await addBlock(payload);
      }
      this.setState({
        blockBusy: false,
        editingBlockId: null,
        blockForm: { date: toYMD(new Date()), fullDay: true, startHour: 14, endHour: 22, tableId: '', description: '' },
        message: { type: 'success', text: editandoId != null ? 'Bloqueo actualizado.' : 'Bloqueo creado.' },
      });
      this.loadBlocks();
      this.loadAgenda();
    } catch (e) {
      this.setState({ blockBusy: false, message: { type: 'danger', text: e.message } });
    }
  };

  // Carga el bloqueo en el form de arriba. "Día completo" se deduce del rango guardado.
  editBlock = (b) => {
    this.setState({
      editingBlockId: b.id,
      blockForm: {
        date: toYMD(new Date(`${String(b.date).slice(0, 10)}T12:00:00`)),
        fullDay: Number(b.start_hour) === 0 && Number(b.end_hour) === 24,
        startHour: Number(b.start_hour),
        endHour: Number(b.end_hour),
        tableId: b.table_id || '',
        description: b.description || '',
      },
      message: null,
    });
  };

  cancelEditBlock = () => this.setState({
    editingBlockId: null,
    blockForm: { date: toYMD(new Date()), fullDay: true, startHour: 14, endHour: 22, tableId: '', description: '' },
  });

  deleteBlock = async (id) => {
    if (!window.confirm('¿Eliminar este bloqueo?')) return;
    try {
      await removeBlock(id);
      this.loadBlocks();
      this.loadAgenda();
    } catch (e) {
      this.setState({ message: { type: 'danger', text: e.message } });
    }
  };

  // Qué pasó con la plata al crear la reserva. El operador tiene que saberlo sin ir al
  // listado: "creada" no dice si quedó algo por cobrar cuando la persona llegue.
  renderWalkinChargeSummary(result) {
    const method = (result.reservation && result.reservation.payment_method) || null;
    if (result.reservation && Number(result.reservation.cover_free) === 1) {
      return <span>Mesa <strong>gratis</strong>: no se cobra entrada (el consumo sí).</span>;
    }
    if (result.deferred) {
      return <span>Entradas <strong>impagas</strong>: se cobran desde la cuenta de la mesa cuando lleguen.</span>;
    }
    if (method === 'mercadopago') {
      return <span>Queda <strong>pendiente</strong> hasta que el cliente pague el link de MercadoPago.</span>;
    }
    return <span>Entradas <strong>cobradas</strong> ({PAYMENT_METHOD_LABEL[method] || '—'}).</span>;
  }

  renderWalkinModal() {
    const w = this.state.walkin;
    if (!w) return null;
    const tables = this.state.agenda.tables || [];
    const av = this.state.walkinAvailability;
    const result = this.state.walkinResult;
    // ENI-198: el combo tiene que respetar el horario DEL DÍA elegido. Leía el schedule
    // global y nunca aplicaba los weekdayOverrides, así que un viernes que cierra a las 24
    // ofrecía sólo hasta las 21 — y el backend, que sí resuelve por fecha (scheduleForDate),
    // habría aceptado 22 y 23. El helper ya existía en reservationsApi y SalonView lo usa.
    //
    // `??` y no `||`: con `openHour: 0` (un local que abre a medianoche) el `||` lo pisaba
    // por el default 14.
    const daySchedule = scheduleForDate(this.state.scheduleForm || {}, w.date);
    const open = daySchedule.openHour ?? 14;
    const last = daySchedule.lastReservation ?? 21;
    const hours = [];
    for (let h = open; h <= last; h++) hours.push(h);

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto' }}>
        <div className="card card-small" style={{ maxWidth: 640, width: '92%', marginTop: 50, marginBottom: 50 }}>
          <div className="card-header border-bottom d-flex justify-content-between align-items-center">
            <h6 className="m-0">Nueva reserva de mesa</h6>
            <button type="button" className="btn btn-sm btn-white" onClick={this.closeWalkin}>✕</button>
          </div>
          <div className="card-body">
            {result ? (
              <div>
                <div className="alert alert-success">
                  Reserva creada en <strong>{result.table ? result.table.label : 'mesa asignada'}</strong> — estado{' '}
                  <strong>{result.reservation && ((RESERVATION_STATUS[result.reservation.status] || {}).label || result.reservation.status)}</strong>.
                  <div className="mt-1">
                    {this.renderWalkinChargeSummary(result)}
                  </div>
                </div>
                {result.transferInfo && (
                  <p className="text-muted">Transferencia: {result.transferInfo.bank} · {result.transferInfo.account} · {result.transferInfo.name}</p>
                )}
                {result.preference && result.preference.init_point && (
                  <a className="btn btn-primary btn-sm" href={result.preference.init_point} target="_blank" rel="noreferrer">Abrir pago Mercado Pago</a>
                )}
                {result.paymentError && (
                  <div className="alert alert-warning">La reserva quedó pendiente: no se pudo generar el pago de Mercado Pago.</div>
                )}
                <div className="mt-3">
                  <button type="button" className="btn btn-white btn-sm mr-1" onClick={this.openWalkin}>Otra reserva</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={this.closeWalkin}>Cerrar</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="form-row">
                  <div className="form-group col-4">
                    <label><small>Fecha</small></label>
                    <input type="date" className="form-control form-control-sm" value={w.date}
                      onChange={(e) => this.setWalkin({ date: e.target.value })} />
                  </div>
                  <div className="form-group col-4">
                    <label><small>Hora</small></label>
                    <select className="form-control form-control-sm" value={w.startHour}
                      onChange={(e) => this.setWalkin({ startHour: Number(e.target.value) })}>
                      {hours.map((h) => <option key={h} value={h}>{pad(h)}:00</option>)}
                    </select>
                  </div>
                  <div className="form-group col-4">
                    <label><small>{w.members.length ? 'Jugadores que pagan' : 'Jugadores'}</small></label>
                    <input type="number" min={w.members.length ? 0 : 1} className="form-control form-control-sm" value={w.playerCount}
                      onChange={(e) => this.setWalkin({ playerCount: Number(e.target.value) })} />
                  </div>
                </div>

                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-white" onClick={this.checkWalkinAvailability}>Verificar disponibilidad</button>
                  {av && (
                    <span className={`ml-2 badge ${av.available ? 'badge-success' : 'badge-danger'}`}>
                      {av.available ? `Disponible${av.warning ? ' · ' + av.warning : ''}` : `No disponible${av.reason ? ' · ' + av.reason : ''}`}
                    </span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group col-6">
                    <label><small>Mesa</small></label>
                    <select className="form-control form-control-sm" value={w.tableId}
                      onChange={(e) => this.setWalkin({ tableId: e.target.value })}>
                      <option value="">Automática</option>
                      {tables.map((t) => <option key={t.id} value={t.id}>{t.label} (cap. {t.capacity})</option>)}
                    </select>
                  </div>
                  <div className="form-group col-6">
                    <label><small>Juego demo (opcional)</small></label>
                    <select className="form-control form-control-sm" value={w.gameCode}
                      onChange={(e) => this.setWalkin({ gameCode: e.target.value })}>
                      <option value="">Sin juego</option>
                      {this.state.demoGames.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-4">
                    <label><small>Cédula</small></label>
                    <input type="text" className="form-control form-control-sm" placeholder="Cédula"
                      value={w.customer.cedula}
                      onChange={(e) => { this.setWalkinCustomer({ cedula: e.target.value, accountId: null }); this.setState({ walkinClientHint: null }); }}
                      onBlur={this.lookupWalkinClient} />
                    {this.state.walkinClientHint && (
                      <small className={this.state.walkinClientHint.type === 'ok' ? 'text-success' : 'text-muted'}>{this.state.walkinClientHint.text}</small>
                    )}
                  </div>
                  <div className="form-group col-8">
                    <label><small>Nombre</small></label>
                    <input type="text" className="form-control form-control-sm" placeholder="Nombre"
                      value={w.customer.name} onChange={(e) => this.setWalkinCustomer({ name: e.target.value })} />
                  </div>
                </div>

                {/* ENI-268: mesa gratis. El checkbox reemplaza al cobro entero — elegir medio de
                    pago para una mesa que no paga es una contradicción en pantalla. */}
                <div className="form-group mb-2">
                  <div className="custom-control custom-checkbox">
                    <input type="checkbox" className="custom-control-input" id="walkin-cover-free"
                      checked={!!w.coverFree}
                      onChange={(e) => this.setWalkin({ coverFree: e.target.checked })} />
                    <label className="custom-control-label" htmlFor="walkin-cover-free">
                      <small>Entrada gratis (nadie de esta mesa paga, tampoco quien se sume después)</small>
                    </label>
                  </div>
                </div>
                {!w.coverFree && (
                <div className="form-row">
                  <div className="form-group col-6">
                    <label><small>Cobro de la entrada</small></label>
                    <select className="form-control form-control-sm" value={w.deferCover ? 'later' : 'now'}
                      onChange={(e) => {
                        const later = e.target.value === 'later';
                        // Diferir con 'mercadopago' elegido no difiere nada: el backend prioriza
                        // MP, genera el link y deja la reserva 'pending'. Si quedó de una vuelta
                        // anterior, se vuelve al default (el resto de los medios se respetan:
                        // son cómo piensa pagar cuando llegue).
                        const dropMp = later && w.paymentMethod === 'mercadopago';
                        this.setWalkin({
                          deferCover: later,
                          ...(dropMp ? { paymentMethod: DEFAULT_PAYMENT_METHOD } : {}),
                        });
                      }}>
                      <option value="later">Cobrar cuando lleguen</option>
                      <option value="now">Cobrar ahora</option>
                    </select>
                    <small className="text-muted">
                      {w.deferCover
                        ? 'Queda impaga: se cobra desde la cuenta de la mesa cuando llegan.'
                        : 'Se factura ahora (ticket + movimiento de caja).'}
                    </small>
                  </div>
                  {!w.deferCover && (
                    <div className="form-group col-6">
                      <label><small>Método de pago</small></label>
                      <select className="form-control form-control-sm" value={w.paymentMethod}
                        onChange={(e) => this.setWalkin({ paymentMethod: e.target.value })}>
                        {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>)}
                      </select>
                      {w.paymentMethod === 'mercadopago' && (
                        <small className="text-muted">Queda pendiente hasta que el cliente pague el link.</small>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Socios con mesa sin cargo ese día: cada uno suma una persona gratis. */}
                {!w.coverFree && (
                  <div className="mb-3">
                    <label className="d-block"><small>Socios (mesa sin cargo)</small></label>
                    {w.members.map((m) => (
                      <div key={m.cedula} className="d-flex align-items-center mb-1" style={{ gap: 8, fontSize: 13 }}>
                        <span className="text-success">✓</span> {m.cedula} <span className="text-muted">· {m.plan}</span>
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => this.removeWalkinMember(m.cedula)}>Quitar</button>
                      </div>
                    ))}
                    <div className="form-row">
                      <div className="col-6"><input type="text" className="form-control form-control-sm" placeholder="Cédula del socio"
                        value={w.memberCedula} onChange={(e) => this.setWalkin({ memberCedula: e.target.value, memberError: null })}
                        onKeyDown={(e) => { if (e.key === 'Enter') this.addWalkinMember(); }} /></div>
                      <div className="col-auto"><button type="button" className="btn btn-sm btn-white" onClick={this.addWalkinMember}>+ Socio</button></div>
                    </div>
                    {w.memberError && <small className="text-danger d-block mt-1">{w.memberError}</small>}
                    {w.members.length > 0 && (
                      <small className="text-muted d-block mt-1">Mesa de {Number(w.playerCount) + w.members.length}: {w.playerCount} que pagan + {w.members.length} socio{w.members.length > 1 ? 's' : ''} sin cargo.</small>
                    )}
                  </div>
                )}

                <div className="mb-2">
                  <label className="d-block"><small>Invitados (opcional)</small></label>
                  {w.guests.map((g, i) => (
                    <div className="form-row mb-1" key={i}>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Nombre"
                        value={g.name} onChange={(e) => this.setWalkinGuest(i, { name: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Cédula"
                        value={g.cedula} onChange={(e) => this.setWalkinGuest(i, { cedula: e.target.value })} /></div>
                      <div className="col-auto"><button type="button" className="btn btn-sm btn-white" onClick={() => this.removeWalkinGuest(i)}>✕</button></div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-white" onClick={this.addWalkinGuest}>+ Invitado</button>
                </div>

                <hr />
                <div className="d-flex justify-content-end">
                  <button type="button" className="btn btn-white btn-sm mr-1" onClick={this.closeWalkin}>Cancelar</button>
                  <button type="button" className="btn btn-primary btn-sm" disabled={this.state.walkinBusy} onClick={this.submitWalkin}>
                    {this.state.walkinBusy ? '...' : 'Crear reserva'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderBloqueos() {
    const tables = this.state.agenda.tables || [];
    const bf = this.state.blockForm;
    return (
      <div className="p-3">
        <h6>Bloqueos de fecha / mesa</h6>
        <p className="text-muted">Bloqueá un día completo o una franja (eventos, feriados), para una mesa puntual o todas. El horario general y el precio se configuran en <strong>Configuración</strong> del admin.</p>
        <div className="card card-small mb-3">
          <div className="card-body py-3">
            <div className="form-row align-items-end">
              <div className="form-group col-6 col-md-3 mb-2">
                <label><small>Fecha</small></label>
                <input type="date" className="form-control form-control-sm" value={bf.date}
                  onChange={(e) => this.setBlockForm({ date: e.target.value })} />
              </div>
              <div className="form-group col-6 col-md-3 mb-2">
                <label className="d-block"><small>Alcance</small></label>
                <label className="mr-2"><input type="checkbox" checked={bf.fullDay} onChange={(e) => this.setBlockForm({ fullDay: e.target.checked })} /> Día completo</label>
              </div>
              {!bf.fullDay && (
                <>
                  <div className="form-group col-3 col-md-1 mb-2">
                    <label><small>Desde</small></label>
                    <input type="number" className="form-control form-control-sm" value={bf.startHour}
                      onChange={(e) => this.setBlockForm({ startHour: Number(e.target.value) })} />
                  </div>
                  <div className="form-group col-3 col-md-1 mb-2">
                    <label><small>Hasta</small></label>
                    <input type="number" className="form-control form-control-sm" value={bf.endHour}
                      onChange={(e) => this.setBlockForm({ endHour: Number(e.target.value) })} />
                  </div>
                </>
              )}
              <div className="form-group col-6 col-md-2 mb-2">
                <label><small>Mesa</small></label>
                <select className="form-control form-control-sm" value={bf.tableId}
                  onChange={(e) => this.setBlockForm({ tableId: e.target.value })}>
                  <option value="">Todas</option>
                  {tables.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group col-6 col-md-2 mb-2">
                <label><small>Motivo</small></label>
                <input type="text" className="form-control form-control-sm" value={bf.description}
                  onChange={(e) => this.setBlockForm({ description: e.target.value })} />
              </div>
              <div className="form-group col-12 col-md-12 mb-2">
                <button type="button" className="btn btn-primary btn-sm" disabled={this.state.blockBusy} onClick={this.submitBlock}>
                  {this.state.editingBlockId != null ? 'Guardar cambios' : 'Crear bloqueo'}
                </button>
                {this.state.editingBlockId != null && (
                  <button type="button" className="btn btn-white btn-sm ml-2" disabled={this.state.blockBusy} onClick={this.cancelEditBlock}>Cancelar</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {!this.state.blocksLoaded ? (
          <p className="text-muted">Cargando bloqueos…</p>
        ) : this.state.blocks.length === 0 ? (
          <p className="text-muted">No hay bloqueos próximos.</p>
        ) : (
          <table className="table mb-0">
            <thead className="bg-light">
              <tr>
                <th className="border-0">Fecha</th>
                <th className="border-0">Horario</th>
                <th className="border-0">Mesa</th>
                <th className="border-0">Motivo</th>
                <th className="border-0"></th>
              </tr>
            </thead>
            <tbody>
              {this.state.blocks.map((b) => (
                <tr key={b.id}>
                  <td>{fmtYMD(b.date)}</td>
                  <td>{Number(b.start_hour) === 0 && Number(b.end_hour) === 24
                    ? 'Todo el día'
                    : `${pad(b.start_hour)}:00–${pad(b.end_hour)}:00`}</td>
                  <td>{b.table_id ? (tables.find((t) => t.id === b.table_id) || {}).label || `#${b.table_id}` : 'Todas'}</td>
                  <td>{b.description || '—'}</td>
                  <td className="text-nowrap">
                    <button type="button" className="btn btn-sm btn-white mr-1" onClick={() => this.editBlock(b)}>Editar</button>
                    <button type="button" className="btn btn-sm btn-white" onClick={() => this.deleteBlock(b.id)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  render() {
    if (!this.state.checked) return (<div></div>);
    const { tab, message } = this.state;

    return (
      <div className="container-fluid">
        <div className="row">
          <SidebarComponent itemSelected={31} role={this.state.role} />
          <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
            <NavbarComponent role={this.state.role} />
            <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '80px' }}>
              <HeaderComponent title="Operación" subtitle="Reservas de mesa" />

              <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                  <ul className="nav nav-pills m-0">
                    <li className="nav-item">
                      <button type="button" className={`btn btn-sm mr-1 ${tab === 'salon' ? 'btn-primary' : 'btn-white'}`}
                        onClick={() => this.setTab('salon')}>Salón</button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`btn btn-sm mr-1 ${tab === 'listado' ? 'btn-primary' : 'btn-white'}`}
                        onClick={() => this.setTab('listado')}>Listado</button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`btn btn-sm mr-1 ${tab === 'bloqueos' ? 'btn-primary' : 'btn-white'}`}
                        onClick={() => this.setTab('bloqueos')}>Bloqueos</button>
                    </li>
                  </ul>
                  {/* "en el acto" describía el walk-in original (gente presente, cobro al toque);
                      hoy este form es el de reservas —fecha, hora, disponibilidad— y el default
                      es cobrar cuando lleguen. Sentar a alguien que ya está va por el salón. */}
                  <button type="button" className="btn btn-sm btn-accent" onClick={this.openWalkin}>+ Nueva reserva</button>
                </div>

                {message && (
                  <div className={`alert alert-${message.type} m-3`}>{message.text}</div>
                )}

                {tab === 'salon' && this.renderSalon()}
                {tab === 'listado' && this.renderListado()}
                {tab === 'bloqueos' && this.renderBloqueos()}
              </div>
            </div>
          </main>
        </div>

        {this.renderDetailModal()}
        {this.renderWalkinModal()}
      </div>
    );
  }
}

export default ReservationsPage;
